import {Backdrop, Box, CircularProgress, Grid} from "@mui/material";
import React, {useMemo} from "react";
import {CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,} from "recharts";
import {FilledCircleIcon} from "../../assets/images/Icons";
import {formatCash} from "../../utils/functions/common";

const colorMapper = {
    "#12794F": "#61D5A9",
    "#EA9196": "#FEBDB6",
    "#46508D": "#CACDFE",
    "#4470C1": "#84B8FD",
};

const symbolMapper = {
    cost: "€",
    spent: "€",
    generated: "€",
    cpc: "€",
    clicks: "",
    revenue_perclick: "€",
    bookingcount: "",
    conversion: "%",
    roi: "NET",
};

const BiAxialLineChart = ({menuOptions, data, colors, compareSeries, loading = false }) => {
    const lines = useMemo(() => {
        if (!data?.length) return [];
        const allKeys = new Set();
        data.forEach((point) => {
            Object.keys(point).forEach((k) => { if (k !== "name") allKeys.add(k); });
        });
        return [...allKeys];
    }, [data]);

    const originalLines = lines.filter((line) => !line.startsWith("compare"));
    const compareLines = lines.filter((line) => line.startsWith("compare"));
    const idToLabelMap = useMemo(() => {
        return menuOptions.reduce((map, option) => {
            map[option.id] = option.label;
            return map;
        }, {});
    }, [menuOptions]);

    // Determine which axis each line should use based on data scale
    const getAxisAssignments = useMemo(() => {
        if (!data?.length || originalLines.length === 0) return { left: [], right: [] };

        // Calculate average values for each metric to determine scale
        const avgValues = originalLines.map(lineKey => {
            const values = data.map(d => d[lineKey]).filter(v => v != null && !isNaN(v));
            const sum = values.reduce((acc, val) => acc + val, 0);
            return { lineKey, avg: values.length > 0 ? sum / values.length : 0 };
        });

        // Sort by average value
        avgValues.sort((a, b) => b.avg - a.avg);

        // If we have multiple metrics, split them between left and right axes
        // Put higher-scale metrics on the left, lower-scale on the right
        const midpoint = Math.ceil(avgValues.length / 2);
        
        if (avgValues.length === 1) {
            return { left: [avgValues[0].lineKey], right: [] };
        } else if (avgValues.length === 2) {
            return { 
                left: [avgValues[0].lineKey], 
                right: [avgValues[1].lineKey] 
            };
        } else {
            // For 3+ metrics, try to balance the axes
            return {
                left: avgValues.slice(0, midpoint).map(v => v.lineKey),
                right: avgValues.slice(midpoint).map(v => v.lineKey)
            };
        }
    }, [data, originalLines]);

    const CustomTooltip = ({ payload }) => {
        const originalLinesTooltip = payload.filter((line) => !line.dataKey.startsWith("compare"));
        const compareLinesTooltip = payload.filter((line) => line.dataKey.startsWith("compare"));

        return (
            <div
                style={{
                    border: "1px solid #61D5A9",
                    borderRadius: "10px",
                    paddingInline: "5px",
                    paddingBlock: "10px",
                    boxShadow: "0px 10px 10px 0px rgba(0, 0, 0, 0.10)",
                    width: "fit-content",
                    backgroundColor: "#fff",
                }}
            >
                <Grid container xs={12}>
                    <div
                        style={{
                            display: "flex",
                            fontSize: "10px",
                            fontWeight: 700,
                            color: "#9D9D9D",
                        }}
                    >
                        <div style={{ width: "130px" }}>&nbsp;</div>
                        <div style={{ width: "70px", textAlign: "end" }}>
                            {originalLinesTooltip[0]?.payload.name}
                        </div>
                        <div
                            style={{
                                width: compareLinesTooltip?.length === 0 ? "0px" : "70px",
                                textAlign: "end",
                                marginLeft: "20px",
                            }}
                        >
                            {compareLinesTooltip?.length > 0
                                ? compareSeries?.[
                                data?.findIndex((dt) => dt.name === compareLinesTooltip[0]?.payload.name)
                                ] || ""
                                : ""}
                        </div>
                    </div>
                </Grid>
                {originalLinesTooltip?.map((ol) => {
                    return (
                        <div
                            style={{
                                fontSize: "10px",
                                color: "#9D9D9D",
                                fontWeight: 400,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    textTransform: "capitalize",
                                    width: "130px",
                                }}
                            >
                                <span style={{ marginTop: "4px", marginRight: "5px" }}>
                                    <FilledCircleIcon color={ol.stroke} />
                                </span>
                                <span>
                                    {idToLabelMap[ol.dataKey] || ol.dataKey}
                                    {symbolMapper[ol.dataKey] ? ` (${symbolMapper[ol.dataKey]})` : ""}
                                </span>
                            </div>
                            <div style={{ width: "70px", textAlign: "end" }}>{ol.value}</div>

                            <div
                                style={{
                                    width: compareLinesTooltip?.length === 0 ? "0px" : "70px",
                                    textAlign: "end",
                                    marginLeft: "20px",
                                }}
                            >
                                {compareLinesTooltip?.find((cl) => {
                                    const l = cl.dataKey?.split("compare")[1];
                                    return (
                                        originalLinesTooltip?.find((o) => o.dataKey === l) &&
                                        l === ol.dataKey
                                    ) || false;
                                })?.value ?? (
                                    <span
                                        style={{
                                            width: compareLinesTooltip?.length === 0 ? "0px" : "70px",
                                            textAlign: "end",
                                        }}
                                    >
                                        &nbsp;
                                    </span>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const getLeftMargin = () => {
        return -20;
    };
    const getRightMargin = () => {
        return 5;
    };

    return (
        <ResponsiveContainer width="100%" height="100%">
            {loading ? 
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress color="primary" />
            </Box>
            :
                <LineChart
                    width={"100%"}
                    height={300}
                    data={data}
                    margin={{
                        top: 5,
                    right: getRightMargin(),
                    left: getLeftMargin(),
                    bottom: 5,
                }}
            >
                <CartesianGrid vertical={false} strokeWidth={0.5} />
                <XAxis
                    interval="preserveStartEnd"
                    style={{
                        marginTop: "10px",
                        fontSize: "8px",
                        fontWeight: 400,
                        fontFamily: "inter !important",
                        color: "#C4C4C4",
                    }}
                    padding={{ left: 20, right: 20 }}
                    stroke="#C4C4C4"
                    tickLine={{
                        strokeWidth: 0.5,
                        style: { transform: "rotateX(15deg)" }
                    }}
                    axisLine={{
                        strokeWidth: 0.5,
                    }}
                    dataKey="name"
                />

                <YAxis
                    yAxisId="left"
                    tickFormatter={formatCash}
                    tickLine={false}
                    axisLine={false}
                    style={{
                        fontSize: "8px",
                        fontFamily: "inter !important",
                        color: "#C4C4C4",
                        fontWeight: 400,
                    }}
                    tick={{ fill: "#C4C4C4" }}
                />

                {getAxisAssignments.right.length > 0 && (
                    <YAxis
                        yAxisId="right"
                        orientation="right"
                        tickFormatter={formatCash}
                        tickLine={false}
                        axisLine={false}
                        style={{
                            fontSize: "8px",
                            fontFamily: "inter !important",
                            color: "#C4C4C4",
                            fontWeight: 400,
                        }}
                        tick={{ fill: "#C4C4C4" }}
                    />
                )}

                <Tooltip content={<CustomTooltip />} />

                {originalLines.map((lineKey, index) => {
                    const yAxisId = getAxisAssignments.left.includes(lineKey) ? "left" : "right";
                    return (
                        <Line
                            key={lineKey}
                            yAxisId={yAxisId}
                            type="linear"
                            dataKey={lineKey}
                            stroke={colors[index]}
                            strokeWidth={2}
                            dot={false}
                        />
                    );
                })}

                {compareLines.map((lineKey) => {
                    const l = lineKey.split("compare")[1];
                    const originalLineIndex = originalLines.findIndex((ol) => ol === l);
                    const yAxisId = getAxisAssignments.left.includes(l) ? "left" : "right";
                    return (
                        <Line
                            key={lineKey}
                            yAxisId={yAxisId}
                            type="linear"
                            dataKey={lineKey}
                            stroke={colorMapper[colors[originalLineIndex]]}
                            dot={false}
                            strokeDasharray={"3 3"}
                            strokeWidth={2}
                        />
                    );
                })}
            </LineChart>}
        </ResponsiveContainer>
    );
};

export default BiAxialLineChart;
